import { fetchUtils } from 'react-admin';
import simpleRestProvider from 'ra-data-simple-rest';
import { getGlobalData, setGlobalData } from './globalData';
import { apiPath } from './isTest';

const getTime = (value) => new Date(value).getTime();

const softLockPossible = () => {
  const info = getGlobalData('info');
  if (!info || info.back !== true || !info.parentexpiry || parseInt(info.db_id, 10) <= 0) {
    return false;
  }
  const parentExpiry = getTime(info.parentexpiry);
  return !!parentExpiry && parentExpiry > Date.now();
}

const softLockExpired = () => {
  const info = getGlobalData('info');
  if (!info || !info.expiry || !softLockPossible()) {
    return false;
  }
  const expiry = getTime(info.expiry);
  if (!expiry) {
    return false;
  }
  return expiry - 30000 <= Date.now();
}

const isExpiredTokenError = (e) => {
  const status = parseInt(e.status, 10);
  const code = parseInt(e.body?.error, 10);
  return (status === 401 || status === 402) && (code === 11 || code === 12);
}

const requestSoftLock = () => {
  window.dispatchEvent(new Event('bw-session-lock'));
}

const httpClient = async (url, options = {}) => {
  const isAuthToken = url.indexOf('/authToken') >= 0;
  if (!isAuthToken && softLockExpired()) {
      requestSoftLock();
      throw new Error('Session locked');
  }
  if (!options.headers || !options.headers.set) {
      options.headers = new Headers({ Accept: 'application/json' });
  }
  var token = getGlobalData('token');
  if (!token) {
    token = localStorage.getItem('token')
  }
  options.headers.set('Authorization', token);
  let response = null;
  try {
    response = await fetchUtils.fetchJson(url, options);
  }
  catch (e) {
    if (!isAuthToken && softLockPossible() && (softLockExpired() || isExpiredTokenError(e))) {
        requestSoftLock();
        throw new Error('Session locked');
    }
    throw e;
  }
  const headerState = response.headers?.get('Bw-Connection');
  const softExpire = response.headers?.get('Bw-Soft-Expire');
  if (headerState || softExpire) {
    try {
      const info = getGlobalData('info') || JSON.parse(localStorage.getItem('info') || '{}');
      const connection = headerState ? JSON.parse(headerState) : null;
      const nextInfo = {
          ...info,
          ...(connection && { sw_db_state: connection.db ?? {}, host: connection.host }),
          ...(softExpire && { expiry: softExpire }),
      };
      setGlobalData({ info: nextInfo });
      localStorage.setItem('info', JSON.stringify(nextInfo));
    } catch (e) {
      // ignore invalid header payload
    }
  }
  return response;
}
const dataProvider = simpleRestProvider(apiPath+'v1', httpClient);
const myDataProvider = {
    ...dataProvider,
    delete: (resource, params) => {
        if (!params.data) {
            return dataProvider.delete(resource, params);
        }
        return httpClient(apiPath+'v1/'+resource+'/'+encodeURIComponent(params.id), {
            method: 'DELETE',
            body: JSON.stringify(params.data),
            headers: new Headers({ 'Content-Type': 'application/json' }),
        }).then(({ json }) => ({ data: json }));
    },
    update: (resource, params) => {
        const uploadEPs = {bwhAgent:true, bwhImage:true, bwhTranslation:true, setImage:true, 
        bwhRoomcategory:true, bwhService:true, setRoomcategory:true, setBookingsheet: true };
        if (!uploadEPs[resource]) {
            // fallback to the default implementation
            return dataProvider.update(resource, params);
        }
        var newFiles = [];
        for (var k in params.data) {
          var v = params.data[k];
          if (v && v.rawFile) {
            v.name = k;
            newFiles.push(v);
          }
          if (v && Array.isArray(v)) {
            for (var i=0;i<v.length;i++) {
              var v2 = v[i];
              if (v2 && v2.rawFile) {
                v2.name = k;
                newFiles.push(v2);
              }
            }  
          }
        }
        return Promise.all(newFiles.map(convertFileToBase64))
            .then(base64File => base64File.map(file64 => ({
                src: file64
            })))
            .then((transformedNewFile) => dataProvider.update(resource, {
                ...params,
                data: {
                    ...params.data,
                    filesdata: [...transformedNewFile],
                    fileinfo: [...newFiles]
                },
            }));
    },
    create: (resource, params) => {
        const uploadEPs = {bwhImage:true, setImage:true, setRoomcategory:true };
        if (!uploadEPs[resource]) {
            // fallback to the default implementation
            return dataProvider.create(resource, params);
        }
        var newFiles = [];
        for (var k in params.data) {
          var v = params.data[k];
          if (v && v.rawFile) {
            v.name = k;
            newFiles.push(v);
          }
        }
        return Promise.all(newFiles.map(convertFileToBase64))
            .then(base64File => base64File.map(file64 => ({
                src: file64
            })))
            .then((transformedNewFile) => dataProvider.create(resource, {
                ...params,
                data: {
                    ...params.data,
                    filesdata: [...transformedNewFile],
                    fileinfo: [...newFiles]
                },
            }));
    },
};

/**
 * Convert a `File` object returned by the upload input into a base 64 string.
 * That's not the most optimized way to store images in production, but it's
 * enough to illustrate the idea of data provider decoration.
 */
const convertFileToBase64 = file =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.rawFile);

        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
    });

export default myDataProvider;
